import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { Search, Copy, Trophy, TrendingUp, TrendingDown, Activity, Target, Users, Sparkles } from 'lucide-react';

const ASSET_TABS = ['All', 'Forex', 'Crypto', 'Stocks'] as const;
const RISK_COLORS: Record<string, string> = {
  low: 'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]',
  medium: 'bg-yellow-500/15 text-yellow-500',
  high: 'bg-destructive/15 text-destructive',
};

const Marketplace = () => {
  const { user } = useAuth();
  const { tier } = useSubscription();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [assetTab, setAssetTab] = useState<string>('All');
  const [riskFilter, setRiskFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('sharpe');
  const [copyingId, setCopyingId] = useState<string | null>(null);

  const { data: strategies = [], isLoading } = useQuery({
    queryKey: ['marketplace-strategies'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('marketplace_strategies')
        .select('*')
        .eq('is_published', true);
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch publisher names
  const publisherIds = useMemo(() => [...new Set(strategies.map((s: any) => s.publisher_id))], [strategies]);
  const { data: publishers = [] } = useQuery({
    queryKey: ['marketplace-publishers', publisherIds],
    queryFn: async () => {
      if (publisherIds.length === 0) return [];
      const { data } = await supabase.from('profiles').select('id, display_name').in('id', publisherIds);
      return data || [];
    },
    enabled: publisherIds.length > 0,
  });

  const publisherMap = useMemo(() => {
    const m: Record<string, string> = {};
    publishers.forEach((p: any) => {
      const name = p.display_name || 'User';
      m[p.id] = name.slice(0, 2) + '***';
    });
    return m;
  }, [publishers]);

  const filtered = useMemo(() => {
    let result = [...strategies];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((s: any) => s.name.toLowerCase().includes(q) || (s.asset_classes || []).some((c: string) => c.toLowerCase().includes(q)));
    }
    if (assetTab !== 'All') {
      result = result.filter((s: any) => (s.asset_classes || []).some((c: string) => c.toLowerCase() === assetTab.toLowerCase()));
    }
    if (riskFilter !== 'all') {
      result = result.filter((s: any) => s.risk_level === riskFilter);
    }
    result.sort((a: any, b: any) => {
      switch (sortBy) {
        case 'sharpe': return (b.sharpe_ratio || 0) - (a.sharpe_ratio || 0);
        case 'winrate': return (b.win_rate || 0) - (a.win_rate || 0);
        case 'copies': return (b.copy_count || 0) - (a.copy_count || 0);
        case 'newest': return new Date(b.published_at || b.created_at).getTime() - new Date(a.published_at || a.created_at).getTime();
        default: return 0;
      }
    });
    return result;
  }, [strategies, search, assetTab, riskFilter, sortBy]);

  const top5 = useMemo(() =>
    [...strategies].sort((a: any, b: any) => (b.sharpe_ratio || 0) - (a.sharpe_ratio || 0)).slice(0, 5),
    [strategies]
  );

  const handleCopy = async (strategy: any) => {
    if (!user) return;
    if (tier === 'free') {
      toast({ title: 'Upgrade Required', description: 'Upgrade to Pro or Quant to copy strategies.', variant: 'destructive' });
      return;
    }
    setCopyingId(strategy.id);
    try {
      // 1. Get original strategy instance config
      const { data: origParams } = await supabase
        .from('strategy_parameters')
        .select('param_name, param_value')
        .eq('strategy_instance_id', strategy.strategy_instance_id);

      // 2. Create new strategy instance
      const { data: newInstance, error: instErr } = await supabase
        .from('strategy_instances')
        .insert({
          user_id: user.id,
          strategy_name: `${strategy.name} (Copy)`,
          status: 'stopped',
          execution_mode: 'demo',
          risk_mode: strategy.risk_level,
        })
        .select()
        .single();
      if (instErr) throw instErr;

      // 3. Copy parameters
      if (origParams && origParams.length > 0 && newInstance) {
        await supabase.from('strategy_parameters').insert(
          origParams.map((p: any) => ({
            strategy_instance_id: newInstance.id,
            param_name: p.param_name,
            param_value: p.param_value,
          }))
        );
      }

      // 4. Insert copy record
      await supabase.from('strategy_copies').insert({
        user_id: user.id,
        marketplace_strategy_id: strategy.id,
        copied_strategy_instance_id: newInstance?.id,
      });

      // 5. Increment copy_count and tokens_earned
      await supabase.from('marketplace_strategies').update({
        copy_count: (strategy.copy_count || 0) + 1,
        tokens_earned: (strategy.tokens_earned || 0) + 10,
      }).eq('id', strategy.id);

      queryClient.invalidateQueries({ queryKey: ['marketplace-strategies'] });
      toast({ title: 'Strategy Copied!', description: 'Deploy it from your Strategies page.' });
    } catch (err: any) {
      toast({ title: 'Copy Failed', description: err.message, variant: 'destructive' });
    } finally {
      setCopyingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-foreground">Strategy Marketplace</h2>
        <p className="text-sm text-muted-foreground">Discover and copy proven trading strategies</p>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search strategies..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={riskFilter} onValueChange={setRiskFilter}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Risk" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Risk</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Sort" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="sharpe">Sharpe Ratio</SelectItem>
            <SelectItem value="winrate">Win Rate</SelectItem>
            <SelectItem value="copies">Most Copied</SelectItem>
            <SelectItem value="newest">Newest</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Asset class tabs */}
      <div className="flex gap-2">
        {ASSET_TABS.map(tab => (
          <Button key={tab} variant={assetTab === tab ? 'default' : 'outline'} size="sm" onClick={() => setAssetTab(tab)}>
            {tab}
          </Button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_280px] gap-6">
        {/* Strategy Cards */}
        <div className="space-y-4">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : filtered.length === 0 ? (
            <Card className="bg-card/40 border-border/30">
              <CardContent className="p-12 text-center">
                <p className="text-muted-foreground">No strategies found. Be the first to publish!</p>
              </CardContent>
            </Card>
          ) : (
            filtered.map((s: any) => (
              <Card key={s.id} className="bg-card/40 border-border/30 hover:border-primary/30 transition-colors">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-foreground">{s.name}</h3>
                        <Badge className={`text-[10px] h-5 border-0 capitalize ${RISK_COLORS[s.risk_level] || ''}`}>
                          {s.risk_level}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">by {publisherMap[s.publisher_id] || 'An***'}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleCopy(s)}
                      disabled={copyingId === s.id}
                      className="gap-1"
                    >
                      <Copy className="h-3.5 w-3.5" />
                      {copyingId === s.id ? 'Copying...' : 'Copy'}
                    </Button>
                  </div>

                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{s.description}</p>

                  {/* Asset badges */}
                  <div className="flex gap-1 mb-3">
                    {(s.asset_classes || []).map((c: string) => (
                      <Badge key={c} variant="outline" className="text-[10px] h-5 capitalize">{c}</Badge>
                    ))}
                  </div>

                  {/* Metrics */}
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <p className="text-[10px] text-muted-foreground flex items-center gap-1"><Target className="h-3 w-3" />Sharpe</p>
                      <p className="text-sm font-semibold text-foreground">{(s.sharpe_ratio || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground flex items-center gap-1"><Activity className="h-3 w-3" />Win Rate</p>
                      <p className="text-sm font-semibold text-foreground">{((s.win_rate || 0) * 100).toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground flex items-center gap-1"><TrendingUp className="h-3 w-3" />Return</p>
                      <p className={`text-sm font-semibold ${(s.total_return || 0) >= 0 ? 'text-primary' : 'text-destructive'}`}>
                        {((s.total_return || 0) * 100).toFixed(1)}%
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground flex items-center gap-1"><TrendingDown className="h-3 w-3" />Drawdown</p>
                      <p className="text-sm font-semibold text-destructive">{((s.max_drawdown || 0) * 100).toFixed(1)}%</p>
                    </div>
                  </div>

                  <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1">
                    <Users className="h-3 w-3" /> {s.copy_count || 0} traders using this
                  </p>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Leaderboard Sidebar */}
        <div>
          <Card className="bg-card/40 border-border/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Trophy className="h-4 w-4 text-yellow-500" />
                Top Strategies
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {top5.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No strategies yet</p>
              ) : (
                top5.map((s: any, i: number) => (
                  <div key={s.id} className="flex items-center gap-3">
                    <span className="text-xs font-bold text-muted-foreground w-4">#{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate text-foreground">{s.name}</p>
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        <span>Sharpe {(s.sharpe_ratio || 0).toFixed(2)}</span>
                        <span>·</span>
                        <span>{s.copy_count || 0} copies</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {tier === 'free' && (
            <Card className="bg-primary/5 border-primary/20 mt-4">
              <CardContent className="p-4 text-center">
                <Sparkles className="h-6 w-6 text-primary mx-auto mb-2" />
                <p className="text-sm font-medium text-foreground">Upgrade to Copy</p>
                <p className="text-xs text-muted-foreground mt-1">Pro & Quant users can copy strategies</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Marketplace;
